import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useIsOverflowing } from '@/hooks/useIsOverflowing';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType } from '@/types/schema';

import { groupDocumentsByCompatibleTypes } from '../utils/ManageDocumentAmendments.utils';

export type DocumentAction = 'add' | 'remove';

interface AmendmentDocument {
  id: string;
  name: string;
  type: DocumentType;
  isCurrentDocument: boolean;
}

interface SelectableDocument {
  id: string;
  name: string;
  type: DocumentType;
}

interface DocumentCheckboxItemProps {
  doc: SelectableDocument;
  isChecked: boolean;
  onToggle: (
    doc: SelectableDocument,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

/**
 * Individual document checkbox with CSS-based text truncation and
 * a tooltip that appears only when the label text overflows.
 */
function DocumentCheckboxItem({
  doc,
  isChecked,
  onToggle,
}: DocumentCheckboxItemProps) {
  const [nameRef, isNameOverflowing] = useIsOverflowing<HTMLSpanElement>([
    doc.name,
  ]);

  return (
    <Box p={1} sx={{ minWidth: 0 }}>
      <Checkbox
        id={`document-${doc.id}`}
        name={`document-${doc.id}`}
        value={isChecked}
        onChange={(event) => onToggle(doc, event)}
        label={
          <Tooltip title={doc.name} show={isNameOverflowing}>
            <Typography
              ref={nameRef}
              component="span"
              variant="body-xs"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                // display:block needed so the span respects width for overflow detection
                display: 'block',
              }}
            >
              {doc.name}
            </Typography>
          </Tooltip>
        }
      />
    </Box>
  );
}

export interface DocumentChange {
  action: DocumentAction;
  document: AmendmentDocument;
}

interface ManageDocumentsPopoverProps {
  selectable: SelectableDocument[];
  selectedIds: string[];
  onDocumentsChanged: (changes: DocumentChange[]) => void;
  onAddNewDocument: () => void;
}

export function ManageDocumentsPopover({
  selectable,
  selectedIds,
  onDocumentsChanged,
  onAddNewDocument,
}: ManageDocumentsPopoverProps) {
  const [localChecked, setLocalChecked] = useState<string[]>(selectedIds);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setLocalChecked(selectedIds);
  }, [selectedIds]);

  const handleClose = () => {
    setIsOpen(false);
    // Reset local state to match the original selected state
    setLocalChecked(selectedIds);
  };

  const handleToggle = (
    doc: SelectableDocument,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    const next = checked
      ? [...localChecked, doc.id]
      : localChecked.filter((id) => id !== doc.id);
    setLocalChecked(next);
  };

  const handleSave = () => {
    // Determine which documents were added or removed
    const addedIds = localChecked.filter((id) => !selectedIds.includes(id));
    const removedIds = selectedIds.filter((id) => !localChecked.includes(id));

    // Collect all changes
    const changes: DocumentChange[] = [];

    // Add documents that were added
    addedIds.forEach((id) => {
      const doc = selectable.find((d) => d.id === id);
      if (doc) {
        changes.push({
          action: 'add',
          document: {
            id: doc.id,
            name: doc.name,
            type: doc.type,
            isCurrentDocument: false,
          },
        });
      }
    });

    // Add documents that were removed
    removedIds.forEach((id) => {
      const doc = selectable.find((d) => d.id === id);
      if (doc) {
        changes.push({
          action: 'remove',
          document: {
            id: doc.id,
            name: doc.name,
            type: doc.type,
            isCurrentDocument: false,
          },
        });
      }
    });

    // Single callback with all changes
    onDocumentsChanged(changes);
    setIsOpen(false);
  };

  const hasUnsavedChanges = useMemo(() => {
    if (localChecked.length !== selectedIds.length) return true;
    return (
      localChecked.some((id) => !selectedIds.includes(id)) ||
      selectedIds.some((id) => !localChecked.includes(id))
    );
  }, [localChecked, selectedIds]);

  // Group selectable documents by compatible types
  const groups = useMemo(() => {
    return groupDocumentsByCompatibleTypes(selectable);
  }, [selectable]);

  const hasNoDocuments = selectable.length === 0;

  return (
    <ButtonWithPopover
      isOpen={isOpen}
      onClose={handleClose}
      onClick={() => setIsOpen(true)}
      size="sm"
      variant="secondary"
      label="Documents"
      startIcon={PlusIcon}
      popperVariant="menuBelow"
      keepOpen
    >
      <Stack
        sx={{ minWidth: 280, maxWidth: 400 }}
        spacing={hasNoDocuments ? 1 : 0}
      >
        <MenuItem
          icon={<PlusIcon size={16} />}
          label="Add new document"
          onClick={() => {
            setIsOpen(false);
            onAddNewDocument();
          }}
          muiMenuItemProps={{
            // show a divider if there are no documents, because it looks bad otherwise
            divider: hasNoDocuments,
          }}
          sx={{ width: '100%', justifyContent: 'flex-start' }}
        />
        <Box sx={{ flexGrow: 1 }}>
          {Object.values(groups).map(
            ({ label, docs }) =>
              docs.length > 0 && (
                <Box key={label}>
                  <SelectItemGroupLabel label={label} />
                  {docs.map((doc) => (
                    <DocumentCheckboxItem
                      key={doc.id}
                      doc={doc}
                      isChecked={localChecked.includes(doc.id)}
                      onToggle={handleToggle}
                    />
                  ))}
                </Box>
              )
          )}
          {hasNoDocuments && (
            <Typography
              variant="body-s"
              color={COLORS.TEXT.SECONDARY}
              px={2}
              py={1}
            >
              No documents available
            </Typography>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, pt: 1 }}
        >
          <Button
            size="sm"
            variant="primary"
            disabled={!hasUnsavedChanges}
            onClick={handleSave}
          >
            Apply
          </Button>
        </Box>
      </Stack>
    </ButtonWithPopover>
  );
}
